// ModuleItem.jsx
import React, { useState, useEffect } from 'react';
import { Container, Switch, Tooltip, Badge } from "@bsf/force-ui";
import { InfoIcon, FileText, LoaderCircle } from 'lucide-react';
import { __ } from "@wordpress/i18n";
// import './styles/ModuleItem.css';

// Create a queue to manage AJAX requests
const requestQueue = [];

const processQueue = () => {
    if (requestQueue.length === 0) return;

    // Take the first item from the queue and run it
    const currentRequest = requestQueue.shift();
    currentRequest();
};

const ModuleItem = ({ 
    module, 
    showTooltip, 
    updateCounter 
}) => {
    const {
        id,
        icon,
        title,
        description,
        is_pro,
        is_active,
        slug,
        demo_url,
        doc_url
    } = module;


    // Track the active state of the module using React state
    const [isActive, setIsActive] = useState(module.is_active);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Update local state when the module prop changes
        setIsActive(module.is_active);
    }, [module.is_active, updateCounter]);

    const apiCall = (activateModule) => {
        const action = activateModule ? 'uavc_deactivate_module' : 'uavc_activate_module';

        const formData = new window.FormData();
        formData.append('action', action);
        formData.append('nonce', uavcSettingsData.nonce);
        formData.append('module_id', id);

        try {
            fetch(uavcSettingsData.ajax_url, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.text();
            })
            .then(text => {
                try {
                    const data = JSON.parse(text);
                    if (data.success) {
                        setIsActive(!isActive);  // Update the active state after the request
                    } else {
                        console.error('Error in response:', data);
                    }
                } catch (e) {
                    console.error('Error parsing JSON:', e, text);
                }
                setIsLoading(false);  // Always stop the loading spinner
                processQueue();
            })
            .catch(error => {
                console.error('Error during AJAX request:', error);
                setIsLoading(false);
                processQueue();
            });
        } catch (err) {
            console.error('Error during AJAX request:', err);
            setIsLoading(false);
            processQueue();
        }
    };

    const handleSwitchChange = () => {
        if (isLoading) return;

        setIsLoading(true);

        if (isActive) {
            // Add the request to the queue
            setIsActive(false);
            requestQueue.push(() => apiCall(isActive));
        } else {
            // Add the request to the queue
            setIsActive(true);
            requestQueue.push(() => apiCall(isActive));
        }
        
        if (requestQueue.length === 1) {
            // Start processing the queue if no other request is being processed
            processQueue();
        }
    };

    return (
        <Container 
            align="center"
            containerType="flex"
            direction="column"
            justify="between"
            gap=""
            className="h-full uavc-module-container"
        >
            <div className='flex items-center justify-between w-full'>
                <div className={`h-10 w-10 mb-5 flex items-center justify-center uavc-icon`} style={{ fontSize: '12px' }}>
                <img src= {icon?.props?.className} alt="" />
                </div>


                <div className='flex items-center gap-x-2' style={{ marginBottom: '15px' }}>
                         <Switch
                                onChange={handleSwitchChange}
                                size='xs'
                                value={isActive}
                                style={{ outline: 'none' }}
                                className="uavc-remove-ring outline-none"
                            />
                </div>
            </div>

            <div className='flex flex-col w-full'>
                <p className='text-sm font-medium text-text-primary pt-3 m-0 pb-1'>{title}</p>
                <div className='flex items-center justify-between w-full'>
                    {demo_url && (
                        <a href={demo_url} target="_blank" rel="noopener noreferrer" className='text-sm text-text-tertiary m-0 mb-1 uavc-remove-ring uavc-demo-link' style={{ lineHeight: '1.5rem' }}>
                            {__('View Demo', 'ultimate_vc')}
                        </a>
                    )}
                    {showTooltip && (
                        <div className={`${!demo_url ? 'uavc-tooltip-wrap' : ''}`}>
                            <Tooltip
                                arrow
                                content={
                                    <div className="uavc-tooltip-content">
                                        <span className='font-semibold block mb-2'>{title}</span>
                                        <span className='block pt-2 mb-2'>{description}</span>
                                        {doc_url && (
                                            <a href={doc_url} target="_blank" rel="noopener noreferrer" className='cursor-pointer' style={{ color: '#B498E5', textDecoration: 'none' }}>
                                                   <FileText style={{ color: '#B498E5', width: '11px', height: '11px', marginRight: '3px' }} />
                                                {__('Read Documentation', 'ultimate_vc')}
                                            </a>
                                        )}
                                    </div>
                                }
                                placement="bottom"
                                title=""
                                triggers={[
                                    'click'
                                ]}
                                variant="dark"
                                size="xs"
                            >
                                <InfoIcon className='h-5 w-5' size={18} color="#A0A5B2" />
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default ModuleItem;
